import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { bedsSchema } from '@views/pages/Admin/Buildings/components/BuildingItem/schemas';
import { toast } from 'react-toastify';
import { formatApiError } from '@common/errors/error-formatting.ts';
import { useEditBuildingMutation } from '@common/cache';
import { paths } from '@types';
import { BedUnits, BED_UNITS } from '@common/types';
import { useContext, useEffect, useState } from 'react';
import { AdminPanelDrawerContext } from '@views/pages/Admin/context';

type IBedsFormShape = Record<BedUnits, number>;

const useBeds = (
  building: paths[`/building/{record_id}`]['get']['responses']['200']['content']['application/json'],
) => {
  const [isSaveChangesModalOpen, setIsSaveChangesModalOpen] = useState(false);
  const {
    triggerClose,
    triggerTabChange,
    setIsFormClean,
    handleCloseConfirmationModal,
  } = useContext(AdminPanelDrawerContext);
  const { mutateAsync, isPending } = useEditBuildingMutation(building?.id);
  const bedsUnitMap = (building?.beds || []).reduce(
    (acc, bed) => {
      acc[bed.unit] = bed.count;
      return acc;
    },
    {} as Record<BedUnits, number>,
  );

  const initialValues = BED_UNITS.reduce(
    (acc, key) => {
      acc[key as BedUnits] = bedsUnitMap[key as BedUnits] || 0;

      return acc;
    },
    {} as Record<BedUnits, number>,
  );

  const {
    control,
    handleSubmit,
    formState: { isSubmitting, isDirty, errors, isValid },
    reset,
    getValues,
  } = useForm<IBedsFormShape>({
    mode: 'onChange',
    resolver: yupResolver(bedsSchema),
    defaultValues: {
      ...initialValues,
    },
  });

  const submitHandler = async (values: IBedsFormShape) => {
    try {
      const beds = Object.keys(values).map(key => {
        return {
          unit: key as BedUnits,
          count: values[key as BedUnits],
        };
      });

      await mutateAsync({
        building_id: building.id,
        ...building,
        beds,
      });

      toast.success('Changes saved');
      reset(values);
      setIsFormClean(true);
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const onSubmit = handleSubmit(submitHandler);

  const handleCloseSaveChangesModal = () => {
    setIsSaveChangesModalOpen(false);
    handleCloseConfirmationModal();
  };

  const handleDiscardChanges = () => {
    setIsSaveChangesModalOpen(false);
    setIsFormClean(true);
  };

  const handleSaveChanges = async () => {
    onSubmit();
    reset(getValues());
    setIsFormClean(true);
  };

  useEffect(() => {
    if (triggerClose && isDirty) {
      setIsSaveChangesModalOpen(true);
      return;
    }

    if (triggerTabChange && isDirty) {
      setIsSaveChangesModalOpen(true);
      return;
    }

    if (isDirty) {
      setIsFormClean(false);
      return;
    }

    if (triggerClose && !isDirty) {
      setIsFormClean(true);
    }
  }, [triggerClose, isDirty, triggerTabChange]);

  return {
    control,
    onSubmit,
    isSubmitting,
    isSubmitEnabled:
      isDirty && Object.keys(errors).length === 0 && !isSubmitting,
    isSaveChangesModalOpen,
    handleCloseSaveChangesModal,
    handleSaveChanges,
    handleDiscardChanges,
    isValid,
    isEditSubmitting: isPending,
  };
};

export default useBeds;
